import React, { useEffect, useState } from "react";
import $ from "jquery";
import { Edit3, Image as ImageIcon, Plus, Save, Upload, X } from "lucide-react";
import ApiURl from "../../controllers/Api";

const EMPTY_FORM = {
  id: "",
  title: "",
  short_description: "",
  read_time: "5 min read",
  sort_order: 1,
  is_active: "1",
  image_url: "",
  content: [{ type: "paragraph", value: "" }],
};

export default function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const response = await $.getJSON(`${ApiURl}getBlogs.php`);
      if (response?.success && Array.isArray(response.data)) {
        setBlogs(response.data);
      }
    } catch (error) {
      console.error("Error loading blogs:", error);
      setMessage("Could not load blogs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setPreview("");
  };

  const editBlog = (blog) => {
    setForm({
      id: blog.id || "",
      title: blog.title || "",
      short_description: blog.short_description || "",
      read_time: blog.read_time || "5 min read",
      sort_order: blog.sort_order || 1,
      is_active: String(blog.is_active ?? 1),
      image_url: blog.image_path || "",
      content:
        Array.isArray(blog.content) && blog.content.length
          ? blog.content.map((block) => ({
              type: block?.type === "heading" ? "heading" : "paragraph",
              value: block?.value || "",
            }))
          : Array.isArray(blog.paragraphs) && blog.paragraphs.length
            ? blog.paragraphs.map((paragraph) => ({ type: "paragraph", value: paragraph }))
            : [{ type: "paragraph", value: "" }],
    });
    setImageFile(null);
    setPreview(blog.image_url || "");
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateContentBlock = (index, value) => {
    setForm((prev) => ({
      ...prev,
      content: prev.content.map((block, i) =>
        i === index ? { ...block, value } : block
      ),
    }));
  };

  const addNewLineToParagraph = (index) => {
    setForm((prev) => ({
      ...prev,
      content: prev.content.map((block, i) => {
        if (i !== index || block.type !== "paragraph") return block;

        const currentValue = String(block.value || "");
        return {
          ...block,
          value: currentValue.endsWith("\n")
            ? currentValue
            : `${currentValue}\n`,
        };
      }),
    }));
  };

  const handleParagraphKeyDown = (event, index) => {
    if (event.key !== "Enter") return;

    // Enter is an intentional line break.
    event.preventDefault();

    const textarea = event.currentTarget;
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? start;
    const currentValue = textarea.value || "";

    const nextValue =
      currentValue.slice(0, start) +
      "\n" +
      currentValue.slice(end);

    updateContentBlock(index, nextValue);

    requestAnimationFrame(() => {
      const nextCursor = start + 1;
      textarea.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const handleParagraphPaste = (event, index) => {
    // Pasted line breaks should NOT accidentally become frontend line breaks.
    // Only Enter / New Line creates an intentional break.
    const pastedText = event.clipboardData?.getData("text") || "";

    if (!pastedText) return;

    event.preventDefault();

    const textarea = event.currentTarget;
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? start;

    const cleanedText = pastedText
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .replace(/[ \t]*\n+[ \t]*/g, " ")
      .replace(/[ \t]{2,}/g, " ");

    const currentValue = textarea.value || "";
    const nextValue =
      currentValue.slice(0, start) +
      cleanedText +
      currentValue.slice(end);

    updateContentBlock(index, nextValue);

    requestAnimationFrame(() => {
      const nextCursor = start + cleanedText.length;
      textarea.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const addContentBlock = (type) => {
    setForm((prev) => ({
      ...prev,
      content: [...prev.content, { type, value: "" }],
    }));
  };

  const removeContentBlock = (index) => {
    setForm((prev) => {
      const next = prev.content.filter((_, i) => i !== index);
      return {
        ...prev,
        content: next.length ? next : [{ type: "paragraph", value: "" }],
      };
    });
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    setImageFile(file);
    if (file) setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");

    const payload = new FormData();
    payload.append("id", form.id);
    payload.append("title", form.title);
    payload.append("short_description", form.short_description);
    payload.append("read_time", form.read_time);
    payload.append("sort_order", form.sort_order);
    payload.append("is_active", form.is_active);
    payload.append("image_url", form.image_url);

    // New ordered content format: headings and paragraphs can appear in any order.
    payload.append("content_json", JSON.stringify(form.content));

    // Backward compatibility for old storefront code that still reads paragraphs.
    payload.append(
      "paragraphs_json",
      JSON.stringify(
        form.content
          .filter((block) => block.type === "paragraph")
          .map((block) => block.value)
      )
    );

    if (imageFile) payload.append("image", imageFile);

    try {
      const response = await $.ajax({
        url: `${ApiURl}saveBlog.php`,
        type: "POST",
        data: payload,
        processData: false,
        contentType: false,
        dataType: "json",
      });

      if (response?.success) {
        setMessage(response.message || "Blog saved successfully.");
        resetForm();
        fetchBlogs();
      } else {
        setMessage(response?.message || "Failed to save blog.");
      }
    } catch (error) {
      console.error("Error saving blog:", error);
      setMessage("Server error while saving blog.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-blogs-luxury min-h-screen w-full overflow-x-hidden bg-[#F7EFE7] p-3 font-[Kanit] text-[#2B0000] sm:p-5 lg:p-7">
      <div className="mx-auto mb-6 max-w-[1500px] rounded-[24px] border border-[#d8c6b7] bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.14),transparent_34%),linear-gradient(135deg,#fffaf5,#f2e6dc)] p-5 shadow-[0_18px_48px_rgba(43,0,0,0.08)] sm:p-6">
        <h1 className="font-serif text-3xl font-medium tracking-tight text-[#2B0000] sm:text-4xl">Blogs</h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-[#6f5e57]">
          Create, update, organise and control which blog articles appear on the storefront.
        </p>
      </div>

      {message && (
        <div className="mx-auto mb-6 max-w-[1500px] rounded-2xl border border-[#d4af37]/25 bg-[#fff7e6] px-4 py-3 text-sm font-semibold text-[#7d5a19] shadow-[0_10px_25px_rgba(43,0,0,0.05)]">
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mx-auto mb-8 max-w-[1500px] rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] p-4 shadow-[0_22px_60px_rgba(43,0,0,0.08)] sm:p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="font-serif text-2xl font-medium text-[#2B0000]">
            {form.id ? "Update Blog" : "Add New Blog"}
          </h2>
          {form.id && (
            <button
              type="button"
              onClick={resetForm}
              className="rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
            >
              Cancel Edit
            </button>
          )}
        </div>

        {/* Top form area: details left, image right */}
        <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="space-y-4">
            <TextInput
              label="Blog Title"
              value={form.title}
              onChange={(value) => updateForm("title", value)}
              required
            />

            <TextArea
              label="Short Description"
              value={form.short_description}
              onChange={(value) => updateForm("short_description", value)}
              rows={3}
            />

            <div className="grid gap-4 md:grid-cols-3">
              <TextInput
                label="Read Time"
                value={form.read_time}
                onChange={(value) => updateForm("read_time", value)}
              />

              <TextInput
                label="Sort Order"
                type="number"
                value={form.sort_order}
                onChange={(value) => updateForm("sort_order", value)}
              />

              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-[#4a352f]">
                  Status
                </span>

                <select
                  value={form.is_active}
                  onChange={(event) =>
                    updateForm("is_active", event.target.value)
                  }
                  className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </label>
            </div>
          </div>

          {/* Blog image */}
          <div className="self-start rounded-[20px] border border-[#decfc3] bg-[linear-gradient(145deg,#fffaf5,#f3e8dd)] p-4 shadow-[0_14px_36px_rgba(43,0,0,0.05)]">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#2B0000]">
              <ImageIcon className="h-4 w-4 text-[#8f6f38]" />
              Blog Image
            </div>

            <div className="mb-4 h-64 overflow-hidden rounded-[18px] border border-[#d8c6b7] bg-[#fbf3eb] shadow-inner">
              {preview ? (
                <img
                  src={preview}
                  alt="Blog preview"
                  className="h-full w-full bg-[#fffaf5] object-contain"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-[#9b877e]">
                  No image selected
                </div>
              )}
            </div>

            <label className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm font-semibold text-[#2B0000] shadow-sm transition hover:border-[#c59a4a] hover:bg-[#fff7ed]">
              <Upload className="h-4 w-4" />
              Upload Image
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Blog Content - now uses the FULL width */}
        <div className="mt-6 overflow-hidden rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] shadow-[0_22px_60px_rgba(43,0,0,0.08)]">
          <div className="flex flex-col gap-3 border-b border-[#d8c6b7] bg-[linear-gradient(135deg,#fffaf5,#f2e6dc)] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div>
              <h3 className="font-serif text-lg font-medium text-[#2B0000]">
                Blog Content
              </h3>

              <p className="mt-1 text-xs leading-5 text-[#7d6c64]">
                Content blocks are arranged left to right: 1 on the left,
                2 on the right, 3 on the left, 4 on the right.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => addContentBlock("heading")}
                className="inline-flex items-center gap-2 rounded-xl border border-[#b8892e] bg-[#fff8e8] px-4 py-2.5 text-sm font-semibold text-[#6b4a12] transition hover:bg-[#f7e8bd]"
              >
                <Plus className="h-4 w-4" />
                Add Heading
              </button>

              <button
                type="button"
                onClick={() => addContentBlock("paragraph")}
                className="inline-flex items-center gap-2 rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-semibold text-[#f4dfae] shadow-md transition hover:bg-[#4A090B]"
              >
                <Plus className="h-4 w-4" />
                Add Paragraph
              </button>
            </div>
          </div>

          {/* 
            Desktop/tablet:
            Block 1 -> left
            Block 2 -> right
            Block 3 -> left
            Block 4 -> right
            etc.

            Mobile stays one column.
          */}
          <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2 md:items-start sm:p-5">
            {form.content.map((block, index) => (
              <div
                key={index}
                className={`min-w-0 rounded-2xl border p-4 ${
                  block.type === "heading"
                    ? "border-[#d4af37]/35 bg-[#fff8e8]"
                    : "border-[#eadfd5] bg-[#fffdf9]"
                }`}
              >
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="grid h-7 min-w-7 place-items-center rounded-full border border-[#d4af37]/35 bg-white px-2 text-[11px] font-bold text-[#8f6f38]">
                      {index + 1}
                    </span>

                    <span className="truncate text-xs font-bold uppercase tracking-[0.14em] text-[#8f6f38]">
                      {block.type === "heading" ? "Heading" : "Paragraph"}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeContentBlock(index)}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-red-200 bg-[#fff4f1] text-red-600 transition hover:bg-red-600 hover:text-white"
                    aria-label={`Remove ${block.type}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {block.type === "heading" ? (
                  <input
                    type="text"
                    value={block.value}
                    onChange={(event) =>
                      updateContentBlock(index, event.target.value)
                    }
                    placeholder="Example: Understanding Gemstone Properties"
                    className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 font-serif text-lg font-semibold text-[#2B0000] shadow-sm outline-none transition placeholder:font-sans placeholder:text-sm placeholder:font-normal placeholder:text-[#9a8a82] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                  />
                ) : (
                  <div>
                    <textarea
                      rows={5}
                      value={block.value}
                      onChange={(event) =>
                        updateContentBlock(index, event.target.value)
                      }
                      placeholder={`Write the blog paragraph here...\nPress Enter whenever you want a new line inside the SAME paragraph.`}
                      className="w-full resize-y rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm leading-6 text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                    />

                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs leading-5 text-[#8a756d]">
                        Press <strong>Enter</strong> or use <strong>New Line</strong> only when you want a visible frontend line break.
                      </p>

                      <button
                        type="button"
                        onClick={() => addNewLineToParagraph(index)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#d6c4b5] bg-white px-3 py-1.5 text-xs font-semibold text-[#6f5145] transition hover:border-[#c59a4a] hover:bg-[#fff7ed]"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        New Line
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#2B0000] px-5 py-3 text-sm font-semibold text-[#f4dfae] shadow-[0_12px_28px_rgba(43,0,0,0.16)] transition hover:-translate-y-0.5 hover:bg-[#4A090B] disabled:cursor-not-allowed disabled:bg-[#b7a69b] disabled:text-white"
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving..." : form.id ? "Update Blog" : "Add Blog"}
        </button>
      </form>

      <div className="mx-auto max-w-[1500px] overflow-hidden rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] shadow-[0_22px_60px_rgba(43,0,0,0.08)]">
        <div className="border-b border-[#d8c6b7] bg-[linear-gradient(135deg,#fffaf5,#f2e6dc)] px-4 py-4 sm:px-5">
          <h2 className="font-serif text-2xl font-medium text-[#2B0000]">Saved Blogs</h2>
        </div>
        <div className="divide-y divide-[#eadfd5]">
          {loading ? (
            <div className="p-6 text-sm text-[#7d6c64]">Loading blogs...</div>
          ) : blogs.length === 0 ? (
            <div className="p-6 text-sm text-[#7d6c64]">No blogs added yet.</div>
          ) : (
            blogs.map((blog) => (
              <div key={blog.id} className="grid gap-4 p-4 transition hover:bg-[#fff5ea] sm:p-5 md:grid-cols-[120px_1fr_auto] md:items-center">
                <div className="h-24 overflow-hidden rounded-xl border border-[#d8c6b7] bg-[#fbf3eb] shadow-sm">
                  {blog.image_url && <img src={blog.image_url} alt={blog.title} className="h-full w-full bg-[#fffaf5] object-contain" />}
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-serif text-lg font-medium text-[#2B0000]">{blog.title}</h3>
                    <span className={`rounded-full px-2 py-0.5 text-xs ${blog.is_active ? "border border-[#cfe2d1] bg-[#edf7ee] text-[#47704e]" : "border border-[#dfd1c6] bg-[#f3ebe4] text-[#6d5a53]"}`}>
                      {blog.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm leading-6 text-[#6f5e57]">{blog.short_description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => editBlog(blog)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:border-[#c59a4a] hover:bg-[#fff7ed]"
                >
                  <Edit3 className="h-4 w-4" />
                  Edit
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <style>{`
        .admin-blogs-luxury {
          background:
            radial-gradient(circle at 7% 0%, rgba(212,175,55,.10), transparent 28%),
            radial-gradient(circle at 95% 92%, rgba(43,0,0,.04), transparent 30%),
            #F7EFE7;
        }

        .admin-blogs-luxury input,
        .admin-blogs-luxury select,
        .admin-blogs-luxury textarea,
        .admin-blogs-luxury button {
          font: inherit;
        }

        .admin-blogs-luxury ::selection {
          color: #fff7e7;
          background: #7a3131;
        }

        .admin-blogs-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(143,111,56,.5) rgba(212,175,55,.07);
        }

        .admin-blogs-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .admin-blogs-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.07);
        }

        .admin-blogs-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(143,111,56,.5);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .admin-blogs-luxury {
            padding: 10px;
          }
        }
      `}</style>
    </div>
  );
}

function TextInput({ label, value, onChange, type = "text", required = false }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-[#4a352f]">{label}</span>
      <input
        type={type}
        required={required}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
      />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 4 }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-[#4a352f]">{label}</span>
      <textarea
        rows={rows}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
      />
    </label>
  );
}